// Public landing page shown at "/" to logged-out web visitors. Marketing
// surface — explains what the platform does and points visitors to the
// app stores (or the browser app via "Find your church" / "Log in").
//
// Logged-in users never see this page (App.js redirects them to /app).
// Median users skip it too (App.js sends them straight to /login).
//
// Ported from desktop/"new landing page"/index-with-image-file.html. The
// hero screenshot lives in public/ and is referenced by absolute path. The
// "Contact" links scroll to an enquiry section near the bottom that POSTs
// to the submit-church-enquiry edge function (same form the old landing
// page used).
import { useEffect, useState } from 'react'

// ── Wired link targets ──────────────────────────────────────────────────
// App Store listing is in review; the /gb/ id link is valid now and
// resolves to the live listing automatically once approved. Google Play is
// already live (share-tracking param dropped for a clean canonical link).
const APP_STORE_URL  = 'https://apps.apple.com/gb/app/id6776408991'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=uk.co.thbiblecommunity.app'

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

export default function LandingPage({ onNavigate }) {
  // SPA navigation — use the parent's onNavigate so we stay in the app
  // instead of triggering a full reload.
  function go(path) {
    return (e) => { e?.preventDefault?.(); onNavigate?.(path) }
  }
  // Anchor-style scroll to an on-page section (Contact).
  function scrollToId(id) {
    return (e) => {
      e?.preventDefault?.()
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // ── Mobile hamburger menu. The header stays focused on the app-download
  // badges on phones, so Contact / Log in / Find your church collapse into
  // this menu (shown only at ≤860px via CSS). Closes on outside click.
  const [menuOpen, setMenuOpen] = useState(false)
  useEffect(() => {
    if (!menuOpen) return
    function onDocClick(e) {
      if (!e.target.closest?.('.mobile-menu') && !e.target.closest?.('.hamburger-btn')) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [menuOpen])

  // ── Contact form — POSTs to the submit-church-enquiry edge function
  // (same behaviour the previous landing page had).
  const [formStatus, setFormStatus] = useState('idle')  // idle | sending | sent | error
  const [formError,  setFormError]  = useState('')

  async function submitForm(e) {
    e.preventDefault()
    if (formStatus === 'sending') return
    const fd = new FormData(e.currentTarget)
    // "Contact us" box: we collect an email + a message. The
    // submit-church-enquiry function still requires name + church, so we
    // pass neutral placeholders (the team replies to the email anyway).
    const message = String(fd.get('message') || '').trim()
    const payload = {
      name:    'Website contact',
      email:   String(fd.get('email') || '').trim(),
      church:  'General contact',
      message: message || 'Sent from the website "Contact us" box.',
    }
    setFormStatus('sending'); setFormError('')
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-church-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.error) throw new Error(data?.error || `HTTP ${res.status}`)
      setFormStatus('sent')
    } catch (err) {
      setFormStatus('error')
      setFormError(err?.message || 'Could not send — please try again.')
    }
  }

  return (
    <>
      <style>{LANDING_CSS}</style>

      <nav>
        <div className="brand">
          <span className="mark">The Bible Community</span>
        </div>
        <div className="nav-actions">
          <a className="nav-login" href="#contact" onClick={scrollToId('contact')}>Contact</a>
          <a className="nav-login" href="/login" onClick={go('/login')}>Log in</a>
          <a className="nav-cta" href="/login" onClick={go('/login')}>Sign up</a>
        </div>
        <button
          type="button"
          className="hamburger-btn"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}>
          <span/><span/><span/>
        </button>
        {menuOpen && (
          <div className="mobile-menu">
            <a href="#contact"
              onClick={e => { setMenuOpen(false); scrollToId('contact')(e) }}>Contact</a>
            <a href="/login"
              onClick={e => { setMenuOpen(false); go('/login')(e) }}>Log in</a>
            <a href="/login" className="mobile-menu-cta"
              onClick={e => { setMenuOpen(false); go('/login')(e) }}>Sign up</a>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{display:'flex',alignItems:'center',gap:10}}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="#EDE7DA" style={{flexShrink:0}}><path d="M16.4 12.9c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.4 0-2.8.8-3.5 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.5 2.2 2.6 2.2 1 0 1.4-.7 2.7-.7 1.3 0 1.6.7 2.7.6 1.1 0 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.2zM14.3 6.3c.6-.7 1-1.7.9-2.7-.8 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6.9.1 1.8-.5 2.5-1.2z"/></svg>
              Apple app
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{display:'flex',alignItems:'center',gap:10}}>
              <svg viewBox="0 0 24 24" width="18" height="18" style={{flexShrink:0}}><path fill="#E8C87A" d="M3.6 2.3c-.2.2-.3.5-.3.9v17.6c0 .4.1.7.3.9l9.3-9.7L3.6 2.3z"/><path fill="#EDE7DA" d="M16.5 8.4 13 11.9l3.5 3.6 4-2.3c.6-.4.6-1.3 0-1.6l-4-2.3z"/><path fill="#9C9A95" d="M3.6 2.3 12.9 12l3.6-3.6L6.5 2.7c-1-.6-2.2-.7-2.9-.4z"/><path fill="#736F68" d="M3.6 21.7c.5.2 1.3.1 2.9-.8l10-5.5L13 11.9l-9.4 9.8z"/></svg>
              Android app
            </a>
          </div>
        )}
      </nav>

      <header className="wrap">
        <div className="hero">
          <div className="hero-copy">
            <h1 className="reveal d2">Tap any verse to hear what <em>your&nbsp;church</em> said about it</h1>
            <p className="plus reveal d3">Jump straight to the moment it was preached - across thousands of sermons from your church and beyond</p>
            <div className="cta-row reveal d4">
              <StoreBadges />
            </div>
          </div>

          <div className="device">
            <div className="glow"></div>
            <div className="phone">
              <span className="btn vol-up"></span>
              <span className="btn vol-dn"></span>
              <span className="btn power"></span>
              <div className="screen">
                <img src="/hero-app-screenshot.png" alt="The Bible Community app showing a marked verse and sermon notes" />
                <div className="verse-tag" title="3 items on this verse">
                  <span className="ring"></span>
                  <span className="d red"></span>
                  <span className="d blue"></span>
                  <span className="n">3</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="features wrap">
        <h2>Your Bible, enriched with your church's sermon clips.</h2>
        <span className="eyebrow">Plus more community features</span>
        <div className="feat-grid">
          {FEATURES.map(f => (
            <div className="feat" key={f.title}>
              <div className="dot" style={{ background: f.color }}></div>
              <h3>{f.title}</h3>
              <p dangerouslySetInnerHTML={{ __html: f.body }} />
            </div>
          ))}
        </div>
      </section>

      <section className="close" id="get">
        <div className="wrap">
          <h2>Read along with <em>every sermon</em> your church has given.</h2>
          <div className="cta-row">
            <StoreBadges />
          </div>
          <p className="web-line">
            Prefer your browser? <a href="/login" onClick={go('/login')}>Log in</a> or{' '}
            <a href="/login" onClick={go('/login')}>sign up</a> - no download needed.
          </p>
        </div>
      </section>

      {/* CONTACT — not in the original design; added so "Contact" links have a
          real destination. One simple email box; submits to the same
          enquiry edge function. */}
      <section className="contact-section" id="contact">
        <div className="wrap contact-inner">
          <span className="eyebrow">Say hello</span>
          <h2>Contact <em>us</em></h2>
          <p className="contact-lead">
            Leave your email and we'll get back to you as soon as we can.
          </p>
          <form onSubmit={submitForm} className="contact-form">
            <input name="email" type="email" required placeholder="Your email address" />
            <textarea name="message" rows={4} required placeholder="Your message" />
            {formStatus !== 'sent' && (
              <button type="submit" disabled={formStatus === 'sending'}>
                {formStatus === 'sending' ? 'Sending…' : 'Send enquiry'}
              </button>
            )}
            {formStatus === 'sent' && (
              <div className="form-ok">✓ Thanks! We'll be in touch within 24 hours.</div>
            )}
            {formStatus === 'error' && (
              <div className="form-err">{formError || 'Could not send — please try again.'}</div>
            )}
          </form>
        </div>
      </section>

      <footer>
        <div className="wrap foot-inner">
          <span className="foot-brand">The Bible Community</span>
          <div className="foot-links">
            <a href="#contact" onClick={scrollToId('contact')}>Contact</a>
            <a href="/privacy" onClick={go('/privacy')}>Privacy</a>
          </div>
        </div>
      </footer>
    </>
  )
}

// Both app-store badges, reused in the hero and the closing CTA.
function StoreBadges() {
  return (
    <>
      <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        <svg viewBox="0 0 24 24" fill="#EDE7DA"><path d="M16.4 12.9c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.4 0-2.8.8-3.5 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.5 2.2 2.6 2.2 1 0 1.4-.7 2.7-.7 1.3 0 1.6.7 2.7.6 1.1 0 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.2zM14.3 6.3c.6-.7 1-1.7.9-2.7-.8 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6.9.1 1.8-.5 2.5-1.2z"/></svg>
        <span className="t"><small>Download on the</small><span>App Store</span></span>
      </a>
      <a className="store-badge" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
        <svg viewBox="0 0 24 24"><path fill="#E8C87A" d="M3.6 2.3c-.2.2-.3.5-.3.9v17.6c0 .4.1.7.3.9l9.3-9.7L3.6 2.3z"/><path fill="#EDE7DA" d="M16.5 8.4 13 11.9l3.5 3.6 4-2.3c.6-.4.6-1.3 0-1.6l-4-2.3z"/><path fill="#9C9A95" d="M3.6 2.3 12.9 12l3.6-3.6L6.5 2.7c-1-.6-2.2-.7-2.9-.4z"/><path fill="#736F68" d="M3.6 21.7c.5.2 1.3.1 2.9-.8l10-5.5L13 11.9l-9.4 9.8z"/></svg>
        <span className="t"><small>Get it on</small><span>Google Play</span></span>
      </a>
    </>
  )
}

// Feature cards — colours echo the app's coloured verse dots.
const FEATURES = [
  { color: '#D9504E', title: 'Sermon notes',           body: "Every scripture your church has taught from, marked in the text with the sermon attached - ready to rewatch." },
  { color: '#4E83D9', title: 'Community comments',      body: 'See what your congregation noticed in a passage, and add what struck you, verse by verse.' },
  { color: '#E8C87A', title: 'Words of encouragement',  body: 'Leave and receive encouragement tied to scripture &mdash; the people beside you on Sunday, with you all week.' },
  { color: '#5FB89A', title: 'Community board',         body: 'A shared space for your church to post, ask, and stay connected between Sundays.' },
  { color: '#9B82D6', title: 'Church events & reminders', body: 'Services, groups and one-off events in one place - with reminders so nothing slips by.' },
  { color: '#D98BA8', title: 'Prayer requests',        body: "Share what you're carrying and pray for one another, right alongside the Word." },
]

// CSS ported from the new-landing-page prototype. Mounted as a <style> tag
// so it only applies while this component is mounted (mirrors the old
// landing page's approach). prefers-reduced-motion block preserved.
const LANDING_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=JetBrains+Mono:wght@400;500&display=swap');
:root{
  --ink:#070707;
  --panel:#101011;
  --panel-2:#161617;
  --gold:#E8C87A;
  --gold-dim:#C9A961;
  --gold-line:rgba(232,200,122,.42);
  --cream:#EDE7DA;
  --muted:#8C877D;
  --muted-2:#5E5A52;
  --serif:'Source Serif 4',Georgia,serif;
  --sans:'Plus Jakarta Sans',-apple-system,system-ui,sans-serif;
  --mono:'JetBrains Mono',ui-monospace,monospace;
}
*{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{
  background:var(--ink);
  color:var(--cream);
  font-family:var(--sans);
  line-height:1.5;
  -webkit-font-smoothing:antialiased;
  overflow-x:hidden;
}
.wrap{max-width:1160px;margin:0 auto;padding:0 28px}

.eyebrow{
  font-family:var(--mono);
  font-size:12px;
  letter-spacing:.28em;
  text-transform:uppercase;
  color:var(--gold);
}

/* ---------- NAV ---------- */
nav{
  display:flex;align-items:center;justify-content:space-between;
  padding:26px 28px;
  max-width:1160px;margin:0 auto;
  position:relative;z-index:5;
}
.brand{display:flex;align-items:baseline;gap:11px}
.brand .mark{
  font-family:var(--serif);font-size:21px;font-weight:600;color:var(--cream);
}
.nav-actions{display:flex;align-items:center;gap:22px}
.nav-login{
  font-family:var(--sans);font-size:14px;font-weight:500;
  color:var(--cream);text-decoration:none;transition:.2s;
}
.nav-login:hover{color:var(--gold)}
.nav-cta{
  font-family:var(--mono);font-size:12px;letter-spacing:.12em;
  color:var(--gold);text-decoration:none;
  border:1px solid var(--gold-line);border-radius:999px;
  padding:9px 18px;transition:.25s;white-space:nowrap;
}
.nav-cta:hover{background:var(--gold);color:#000;border-color:var(--gold)}

/* hamburger + dropdown — hidden on desktop, shown at <=860px (see media query) */
.hamburger-btn{display:none}
.mobile-menu{display:none}

/* ---------- HERO ---------- */
.hero{
  display:grid;
  grid-template-columns:1.05fr .95fr;
  gap:56px;
  align-items:start;
  padding:40px 0 96px;
}
.hero-copy{max-width:560px;position:relative;z-index:2}
.hero .eyebrow{margin-bottom:18px;display:inline-block}

h1{
  font-family:var(--serif);
  font-weight:500;
  font-size:clamp(40px,5.6vw,68px);
  line-height:1.04;
  letter-spacing:-.01em;
  color:var(--cream);
}
h1 em{
  font-style:italic;
  color:var(--gold);
  white-space:nowrap;
}

.subhead{
  font-family:var(--serif);
  font-style:italic;
  font-size:clamp(19px,2.2vw,23px);
  color:var(--muted);
  margin-top:24px;
  line-height:1.45;
}
.subhead b{color:var(--cream);font-style:normal;font-weight:500}

.plus{
  margin-top:30px;
  font-size:15.5px;
  color:var(--muted);
  max-width:430px;
}

.cta-row{display:flex;gap:14px;margin-top:38px;flex-wrap:wrap}
.store-badge{
  display:flex;align-items:center;gap:11px;
  background:var(--panel);
  border:1px solid rgba(255,255,255,.08);
  border-radius:13px;
  padding:11px 18px 11px 16px;
  text-decoration:none;color:var(--cream);
  transition:.25s;
}
.store-badge:hover{border-color:var(--gold-line);transform:translateY(-2px)}
.store-badge svg{width:23px;height:23px;flex:none}
.store-badge .t{display:flex;flex-direction:column;line-height:1.15}
.store-badge .t small{font-size:9.5px;color:var(--muted);letter-spacing:.04em}
.store-badge .t span{font-size:15px;font-weight:600}

/* ---------- PHONE / HEADER IMAGE ---------- */
.device{
  position:relative;
  justify-self:center;
  width:min(100%,278px);
}
.device .glow{
  position:absolute;inset:-14% -10% -8%;
  background:radial-gradient(60% 50% at 60% 35%,rgba(232,200,122,.20),transparent 70%);
  filter:blur(10px);z-index:0;
  pointer-events:none;   /* decorative — must not intercept clicks on the hero CTA badges */
}
.device .phone{
  position:relative;z-index:1;
  padding:9px;
  border-radius:52px;
  background:linear-gradient(150deg,#28282c,#0b0b0d 52%,#1d1d21);
  box-shadow:
    0 52px 104px -34px rgba(0,0,0,.92),
    0 0 0 1px rgba(255,255,255,.05),
    inset 0 0 0 1px rgba(255,255,255,.06);
}
.device .screen{
  position:relative;
  border-radius:44px;
  overflow:hidden;
  background:#000;
  box-shadow:inset 0 0 0 1.5px rgba(0,0,0,.85);
}
.device .screen img{display:block;width:100%;height:auto}
.device .btn{
  position:absolute;z-index:0;border-radius:2px;
  background:linear-gradient(180deg,#222226,#0a0a0b);
}
.device .btn.vol-up{left:-2.5px;top:22%;width:3px;height:7.5%}
.device .btn.vol-dn{left:-2.5px;top:31.5%;width:3px;height:7.5%}
.device .btn.power{right:-2.5px;top:25%;width:3px;height:11%}

.verse-tag{
  position:absolute;z-index:3;
  top:35.5%;left:81%;
  display:flex;align-items:center;gap:5px;
  background:rgba(12,12,13,.92);
  border:1px solid var(--gold-line);
  border-radius:999px;
  padding:5px 10px 5px 8px;
  box-shadow:0 6px 18px -6px rgba(0,0,0,.8);
  opacity:0;
  animation:tagIn .5s cubic-bezier(.2,.8,.2,1) 1.1s forwards;
}
.verse-tag .ring{
  position:absolute;inset:-1px;border-radius:999px;
  border:1px solid var(--gold);
  animation:pulse 2.6s ease-out 1.6s infinite;
}
.verse-tag .d{width:7px;height:7px;border-radius:50%}
.verse-tag .d.red{background:#D9504E}
.verse-tag .d.blue{background:#4E83D9;margin-left:-3px}
.verse-tag .n{
  font-family:var(--mono);font-size:12px;font-weight:500;
  color:var(--gold);margin-left:2px;
}
@keyframes tagIn{from{opacity:0;transform:scale(.7)}to{opacity:1;transform:scale(1)}}
@keyframes pulse{0%{opacity:.7;transform:scale(1)}70%{opacity:0;transform:scale(1.55)}100%{opacity:0}}

/* ---------- FEATURES ---------- */
.features{padding:92px 0 40px}
.features .eyebrow{display:block;text-align:center;margin-bottom:54px}
.features h2{
  font-family:var(--serif);font-weight:500;
  font-size:clamp(28px,3.4vw,40px);text-align:center;
  color:var(--cream);margin-bottom:16px;
}
.feat-grid{
  display:grid;grid-template-columns:repeat(3,1fr);gap:18px;
}
.feat{
  background:var(--panel);
  border:1px solid rgba(255,255,255,.07);
  border-radius:18px;
  padding:28px 26px 30px;
  transition:.3s;
}
.feat:hover{border-color:var(--gold-line);background:var(--panel-2)}
.feat .dot{
  width:11px;height:11px;border-radius:50%;margin-bottom:18px;
  box-shadow:0 0 0 4px rgba(255,255,255,.04);
}
.feat h3{
  font-family:var(--serif);font-weight:500;font-size:20px;
  color:var(--cream);margin-bottom:9px;
}
.feat p{font-size:14.5px;color:var(--muted);line-height:1.55}

/* ---------- CLOSING CTA ---------- */
.close{
  text-align:center;padding:96px 0 80px;
}
.close h2{
  font-family:var(--serif);font-weight:500;
  font-size:clamp(30px,4.4vw,52px);color:var(--cream);
  line-height:1.08;max-width:760px;margin:0 auto 34px;
}
.close h2 em{font-style:italic;color:var(--gold)}
.close p{color:var(--muted);font-size:16px;margin-bottom:34px}
.close .cta-row{justify-content:center}
.web-line{margin-top:24px;font-size:14.5px;color:var(--muted)}
.web-line a{color:var(--gold);text-decoration:none;border-bottom:1px solid var(--gold-line);transition:.2s}
.web-line a:hover{border-color:var(--gold)}

/* ---------- CONTACT ---------- */
.contact-section{
  border-top:1px solid rgba(255,255,255,.06);
  padding:88px 0 96px;
}
.contact-inner{max-width:600px;text-align:center}
.contact-section .eyebrow{display:block;margin-bottom:18px}
.contact-section h2{
  font-family:var(--serif);font-weight:500;
  font-size:clamp(28px,3.6vw,42px);color:var(--cream);
  line-height:1.1;margin-bottom:16px;
}
.contact-section h2 em{font-style:italic;color:var(--gold)}
.contact-lead{color:var(--muted);font-size:16px;line-height:1.6;margin-bottom:34px}
.contact-form{display:flex;flex-direction:column;gap:13px;text-align:left;max-width:420px;margin:0 auto;width:100%}
.contact-form input,
.contact-form textarea{
  width:100%;
  background:var(--panel);
  border:1px solid rgba(255,255,255,.1);
  border-radius:11px;
  padding:14px 16px;
  font-family:var(--sans);font-size:15px;
  color:var(--cream);
  outline:none;
  transition:border-color .2s;
}
.contact-form input::placeholder,
.contact-form textarea::placeholder{color:var(--muted-2)}
.contact-form input:focus,
.contact-form textarea:focus{border-color:var(--gold-line)}
.contact-form textarea{resize:vertical}
.contact-form button{
  background:var(--gold);color:#000;
  font-family:var(--mono);font-size:13px;font-weight:500;
  letter-spacing:.08em;text-transform:uppercase;
  padding:15px;border-radius:11px;border:none;
  cursor:pointer;transition:.2s;
}
.contact-form button:hover{background:var(--gold-dim)}
.contact-form button:disabled{opacity:.65;cursor:default}
.form-ok{
  text-align:center;padding:15px;
  background:rgba(232,200,122,.08);
  border:1px solid var(--gold-line);border-radius:11px;
  color:var(--gold);font-family:var(--mono);font-size:13px;letter-spacing:.04em;
}
.form-err{
  text-align:center;padding:13px;
  background:rgba(217,80,78,.1);
  border:1px solid rgba(217,80,78,.4);border-radius:11px;
  color:#E78A88;font-size:13px;
}

/* ---------- FOOTER ---------- */
footer{
  border-top:1px solid rgba(255,255,255,.06);
  padding:26px 0;
  font-family:var(--mono);font-size:11px;letter-spacing:.14em;
  color:var(--muted-2);text-transform:uppercase;
}
.foot-inner{display:flex;align-items:center;justify-content:space-between;gap:18px}
.foot-brand{color:var(--muted-2)}
.foot-links{display:flex;gap:26px}
.foot-links a{color:var(--muted);text-decoration:none;transition:.2s}
.foot-links a:hover{color:var(--gold)}
@media (max-width:560px){.foot-inner{flex-direction:column;text-align:center}}

/* reveal-on-load for hero */
.reveal{opacity:0;transform:translateY(14px);animation:rise .8s cubic-bezier(.2,.7,.2,1) forwards}
.reveal.d1{animation-delay:.05s}
.reveal.d2{animation-delay:.18s}
.reveal.d3{animation-delay:.32s}
.reveal.d4{animation-delay:.46s}
.device{opacity:0;animation:rise 1s cubic-bezier(.2,.7,.2,1) .35s forwards}
@keyframes rise{to{opacity:1;transform:translateY(0)}}

@media (max-width:860px){
  /* Header collapses to brand + hamburger so the hero download badges
     stay the focus. The inline links move into the dropdown menu. */
  nav{padding:20px}
  .nav-actions{display:none}

  .hamburger-btn{
    display:inline-flex;flex-direction:column;justify-content:center;gap:5px;
    width:34px;height:34px;padding:5px;margin-left:auto;
    background:transparent;border:none;cursor:pointer;
  }
  .hamburger-btn > span{
    display:block;width:24px;height:2px;border-radius:1px;
    background:var(--cream);transition:.2s;
  }

  .mobile-menu{
    display:flex;flex-direction:column;gap:6px;
    position:absolute;top:100%;left:0;right:0;z-index:20;
    margin:0 20px;
    background:var(--panel);
    border:1px solid var(--gold-line);border-radius:14px;
    padding:14px;
    box-shadow:0 18px 40px -12px rgba(0,0,0,.85);
  }
  .mobile-menu a{
    font-family:var(--sans);font-size:15px;font-weight:500;
    color:var(--cream);text-decoration:none;
    padding:11px 12px;border-radius:9px;transition:.2s;
  }
  .mobile-menu a:hover{background:rgba(255,255,255,.05);color:var(--gold)}
  .mobile-menu .mobile-menu-cta{
    font-family:var(--mono);font-size:13px;letter-spacing:.06em;
    text-align:center;color:#000;background:var(--gold);margin-top:4px;
  }
  .mobile-menu .mobile-menu-cta:hover{background:var(--gold-dim);color:#000}

  .hero{grid-template-columns:1fr;gap:38px;padding:30px 0 44px;text-align:center}
  .hero-copy{margin:0 auto}
  .cta-row{justify-content:center}
  .features{padding:56px 0 24px}
  .feat-grid{grid-template-columns:1fr}
  .close{padding:64px 0 64px}
  .contact-section{padding:56px 0 64px}
}
@media (prefers-reduced-motion:reduce){
  .reveal,.device,.verse-tag{animation:none;opacity:1;transform:none}
  .verse-tag .ring{display:none}
}
`
